
import { API } from '@/api/API'
import ApplyPromoCodesCheckout from '@/checkout/components/ApplyPromoCodesCheckout.vue'
import BlockedCartForm from '@/checkout/components/BlockedCartForm.vue'
import SelectPaymentMethod, { PaymentMethodIdentifier } from '@/checkout/components/SelectPaymentMethod.vue'
import TermsCheckbox from '@/checkout/components/TermsCheckbox.vue'
import VueHcaptcha from '@hcaptcha/vue-hcaptcha'
import { cardIsAllowed } from '@/checkout/helpers/allowedCards'
import type { CompletedOrdersResponse } from '@/checkout/helpers/completing'
import { pushPurchaseEventToGoogleTagManager } from '@/checkout/helpers/googleTagManager'
import { identityDataForStripe } from '@/checkout/helpers/identity'
import { getPaymentPayloads } from '@/checkout/helpers/payloads'
import { CheckoutOptions, PaymentTokens, processPaymentPayloads } from '@/checkout/helpers/processing'
import { walletNames, WalletType } from '@/checkout/helpers/wallets'
import { authorizeCreditCard } from '@/checkout/stripe/helpers'
import StripeWalletPayment from '@/checkout/stripe/StripeWalletPayment'
import TixStripeError from '@/checkout/stripe/TixStripeError'
import FloatingNavigateBack from '@/components/elements/FloatingNavigateBack.vue'
import MembersBanner from '@/components/elements/MembersBanner.vue'
import NavigateBack from '@/components/elements/NavigateBack.vue'
import Price from '@/components/elements/Price.vue'
import CheckoutDonation from '@/components/events/CheckoutDonation.vue'
import IdentityForm from '@/components/forms/IdentityForm.vue'
import { apiErrorMessageOrRethrow, getApiErrorEntity, logInTrackJS, notFoundRoute } from '@/errors/helpers'
import NotFoundError from '@/errors/NotFoundError'
import ValidationError from '@/errors/ValidationError'
import { afterpayData, handleAfterpayRedirectResponse } from '@/helpers/AfterpayHelpers'
import environment, { captchaProvider, portal } from '@/helpers/Environment'
import { getIdentityFormFields } from '@/helpers/IdentityFormHelpers'
import { IdentityFormData, upsertGuestAndGetId } from '@/helpers/IdentityHelpers'
import { changeMemberDetailsInstructions } from '@/helpers/LanguageHelpers'
import { getPostCodeFilterRegularExpression } from '@/helpers/PostCodeAsDiscount'
import { applyPromoCode } from '@/helpers/PromoCodes'
import { memberHasNoEmailAddress } from '@/helpers/RequireMemberEmailAddress'
import { escapeHTML } from '@/helpers/StringHelpers'
import tenant from '@/helpers/Tenant'
import { reportFormValidity } from '@/helpers/Validation'
import type { LanguageStrings } from '@/language/types'
import { clearTimer } from '@/plugins/CartExpire'
import { injectCart, refreshStoredCart } from '@/state/Cart'
import type { Cart } from '@/store/Cart'
import type { ComponentOptions } from '@/types/ComponentOptions'
import type { PaymentIntent, PaymentMethod, Stripe, StripeCardElement, StripeError } from '@stripe/stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { Component, Vue, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'
import {
  getCartPayload,
  getPurchasePayloads,
  isGA4,
  triggerBeginCheckoutEvent,
  triggerPurchaseEvents,
} from '@/checkout/helpers/ga4'
import CartWidget from '../components/cart/CartWidget.vue'
import CheckoutMobileFooter from '../components/cart/CheckoutMobileFooter.vue'
import MobileCartWidget from '../components/cart/MobileCartWidget.vue'
import MobileFooterPortal from '../components/cart/MobileFooterPortal.vue'
import store from '../store/store'
import CaptchaError from '@/errors/CaptchaError'
import { resetReCaptcha } from '@/helpers/CaptchaHelpers'
import type { PayButtonContext } from '@/helpers/CartHelpers'

/**
 * Route-leave events are implemented in afterEach handlers.
 *
 * @see afterCheckoutRoute() in checkout-helpers/completing.ts
 *
 * Matrix of checkout scenarios & features and cart states;
 * @see https://docs.google.com/spreadsheets/d/1L3OMbW1Tc3CHpx3GgkIXvVRpzXamIEtdtqlUzHV4vYw/edit?usp=sharing
 *
 * TODO Use form="" attribute to regroup form inputs and "nest" all three forms on a single route;
 *    - Main checkout form
 *    - Gift cards
 *    - Promo codes
 *
 * @see https://stackoverflow.com/a/54901309
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-form
 *
 * Blocked by IE11 support:
 * @see https://caniuse.com/form-attribute
 */
@Component({
  name: 'CheckoutRoute',
  components: {
    ApplyPromoCodesCheckout,
    Price,
    SelectPaymentMethod,
    IdentityForm,
    BlockedCartForm,
    TermsCheckbox,
    CheckoutDonation,
    NavigateBack,
    CartWidget,
    MobileCartWidget,
    MobileFooterPortal,
    CheckoutMobileFooter,
    FloatingNavigateBack,
    MembersBanner,
    VueHcaptcha,
  },
  computed: mapGetters({
    cart: 'Cart/cart',
    stripeAccountId: 'Cart/stripeAccountId',
    cartHasItems: 'Cart/cartHasItems',
    identityFormConfigurations: 'Cart/identityFormConfigurations',
    memberUser: 'Member/user',
    injectedCartOwner: 'Cart/injectedCartOwner',
    paymentDueByCreditCard: 'Cart/paymentDueByCreditCard',
  }),
})
export default class extends Vue {
  // Data properties
  loading = true
  // TODO Document the difference between processing and submitting
  // TODO Use :inert="processing" to prevent further user input after submit.
  // @see https://developer.chrome.com/articles/inert/
  // @see https://trello.com/c/otDSTvBV
  // @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/inert
  processing = false
  submitting = false
  error: string | null = null
  validationError: string | null = null
  paymentError: string | null = null

  wallet: StripeWalletPayment | null = null
  supportedWallet: WalletType | null = null
  selectedPaymentType: PaymentMethodIdentifier | null = null

  // True if the cart starts free or ever became free since the route initialized.
  // This is used to determine if <CheckoutDonation> should appear above or below the identity form;
  //
  //   - It should be above for initially free carts, and paid carts that become free.
  //   - It should be below for paid carts.
  //   - It should move above for carts that become free, but not the other way;
  //   - It should stay above when a free cart becomes a paid cart. E.g. due to adding a donation on checkout.
  //
  // This promotes donations for free carts, while minimizing flashes of content and significant layout changes when
  // free carts become paid (and vice versa) with digital wallet payment methods.
  cartWasEverFree = false

  zipCodeDiscountRegExp: RegExp | false | null = null
  purchaserEmailAddress: string | null = null
  stripeCardElement: StripeCardElement | null = null
  stripe: Stripe | null = null
  identityFormData: IdentityFormData = {}
  showMobileCart: boolean = false
  hasSentBeginCheckoutEvent = false

  // VueX store properties and methods.
  cart: Cart
  stripeAccountId: string
  cartHasItems: boolean
  identityFormConfigurations: string[]
  memberUser: UserEntity
  paymentDueByCreditCard: number
  /**
   * @deprecated Identity does not indicate the cart is injected and it is not necessarily the owner.
   */
  injectedCartOwner: Identity

  // Can't show Apple Pay form unless it is from a user gesture.
  // Clicks on ReCaptcha do not count (probably because it is in an iframe).
  // TODO If/when we remove ReCaptcha support; Track this state with hCaptcha event callbacks, like `opened`.
  // @see https://github.com/hCaptcha/vue-hcaptcha#callback-events
  lastInteractionWasCaptcha = false
  private hcaptchaToken: string | null = null

  // Options plugin properties.
  t: LanguageStrings['checkoutRoute']
  opt: ComponentOptions['checkoutRoute']

  @Watch('loading')
  handleLoadingChange(loading) {
    if (!loading) {
      this.$nextTick(() => {
        const mainEl = this.$refs.main as HTMLDivElement
        mainEl.addEventListener('focusin', this.handleInputFocus)
      })
    }
  }

  beforeRouteEnter(to, from, next) {
    if (memberHasNoEmailAddress()) {
      next('/user/set-email-address')
      return
    }

    const injecting = injectCart(to.params)
    if (injecting) {
      injecting
        .then(() => next())
        .catch((error) => {
          if (error.response?.status === 403) {
            // User is probably logged in. This scenario is handled in the template.
            next()
          } else if (error instanceof NotFoundError || error.response?.status === 404) {
            next(notFoundRoute(to))
          } else {
            throw error
          }
        })
    } else if (!store.getters['Cart/cartHasItems']) {
      // No cart. Stop loading and return to home.
      next({ name: 'home' })
    } else {
      getPostCodeFilterRegularExpression().then((filter) => {
        if (filter) {
          next((vm) => {
            vm.zipCodeDiscountRegExp = filter
          })
        } else {
          next()
        }
      })
    }
  }

  @Watch('stripeInitializationKey', { immediate: true })
  initializePaymentMethod() {
    if (this.stripePaymentRequired) {
      loadStripe(environment.stripe.publishable_key, { stripeAccount: this.stripeAccountId }).then((stripe) => {
        if (stripe) {
          this.stripe = stripe
          if (this.$route.query.payment_intent_client_secret) {
            this.handleAfterpayPayment()
          } else {
            this.initializeStripeWallet()
          }
        } else {
          this.error =
            'Credit card payment can not be processed because Stripe was unable to load. Try disabling any privacy blockers and ad blockers, and reloading the page.'
        }
      })
    } else {
      this.loading = false
      this.supportedWallet = 'none'
      this.stripe = null
    }
  }

  // Should attempt to initialize stripe whenever stripeAccountId and/or stripePaymentRequired change.
  get stripeInitializationKey(): string {
    return `${this.stripeAccountId}:${this.stripePaymentRequired}`
  }

  handleAfterpayPayment() {
    const params = this.$route.query
    // TODO How to support AfterPay together with Captcha?
    const options = this.checkoutAPIOptions({}, params.purchaser)
    /* eslint-disable no-console */
    console.log('handle afterpay redirect')
    handleAfterpayRedirectResponse(this.stripe!, params.cartId, params.payment_intent_client_secret, options)
      .then(this.onSuccess)
      .catch((e) => {
        // AfterPay failed. Let the customer try again, maybe with a different payment method.
        this.initializeStripeWallet()
        this.onError(e)
      })

    // @ts-ignore
    params.payment_intent_client_secret = undefined
    // @ts-ignore
    params.cartId = undefined
    // @ts-ignore
    params.purchaser = undefined
  }

  initializeStripeWallet() {
    const identityFields = getIdentityFormFields(this.identityFormData, this.identityFormConfigurations, [])
    this.wallet = new StripeWalletPayment(this.stripe!, this.paymentDueByCreditCard, identityFields)

    if (tenant.postCodeAsDiscountCode || tenant.allowedCardBrands) {
      // Do not enable wallet payments if using the "post code as discount" or "allowed card brands" (USOPM) features.
      // They are not compatible with wallet payments.
      this.supportedWallet = 'none'
    } else {
      // Set an initial value for supportedWallet based on silent background check on app-instantiation.
      // This means the UI does not need to wait for the check to complete when instantiating the checkout route.
      // @see Cart store's `initialize()` action.
      this.supportedWallet = store.state['Cart'].wallet

      // The StripeWalletPayment instance from the silent background check on app-init can not be reused because it
      // does not request any payer information. Determining what payer information to request is only possible on
      // checkout, because identity forms change depending on what is in the cart. And requestPayerPhone etc can
      // only be specified on instantiation of Stripe PaymentRequests (which StripeWalletPayments wraps).
      this.wallet.enabled.then((result) => {
        this.supportedWallet = result
      })
    }
  }

  // `immediate` is necessary as the watcher for `initializePaymentMethod()` which sets `supportedWallet` also uses `immediate`.
  @Watch('supportedWallet', { immediate: true })
  observeSupportedWallet() {
    if (this.supportedWallet) {
      this.loading = false
      this.selectedPaymentType = this.supportedWallet === 'none' ? 'stripe_card' : 'stripe_wallet'
    }
  }

  get orderId(): string | undefined {
    return this.$route.params.orderId
  }

  /**
   * @see https://docs.google.com/spreadsheets/d/1L3OMbW1Tc3CHpx3GgkIXvVRpzXamIEtdtqlUzHV4vYw/edit?usp=sharing
   */
  get isModifiable(): boolean {
    if (this.cart.state === 'pending') {
      return this.cart.ownedByPublicIdentity || this.ownedByAuthenticatedUser
    } else {
      return false
    }
  }

  get ownedByAuthenticatedUser(): boolean {
    return this.memberIdentity ? this.memberIdentity.id === this.cart.identity_id : false
  }

  get identityFormFields() {
    const omit = this.usesWallet ? this.wallet!.identityFieldNames : []
    return getIdentityFormFields(this.identityFormData, this.identityFormConfigurations, omit, this.usesAfterpay)
  }

  get usesAfterpay() {
    return this.selectedPaymentType === 'stripe_afterpay'
  }

  get stripePaymentRequired(): boolean {
    return this.paymentDueByCreditCard > 0
  }

  get usesWallet() {
    return this.stripePaymentRequired && this.selectedPaymentType === 'stripe_wallet'
  }

  get memberIdentity(): WithMeta<Identity> | void {
    return store.getters['Member/identityWithMeta'][0]
  }

  get giftee() {
    return store.getters['Cart/giftee']
  }

  get title() {
    if (this.giftee) {
      return `Pay for your Gift to ${this.giftee.name}`
    } else {
      return this.t.title
    }
  }

  get blocked(): boolean {
    return store.getters['Cart/blockingRules']?.length > 0
  }

  get memberMessage() {
    return this.$t('checkoutRoute.memberMessage', {
      email: `<em>${this.memberIdentity!.email}</em>`,
    }) as string
  }

  get changeMemberDetailsInstructions(): string {
    return changeMemberDetailsInstructions()
  }

  get showIdentityForm() {
    // Carts owned by the public identity must be re-assigned on checkout.
    return this.cart.ownedByPublicIdentity
  }

  get walletIdentityDataMessage(): string | false {
    if (this.usesWallet) {
      return this.$t('checkoutRoute.identityDataMessage', { wallet: walletNames[this.supportedWallet!] }) as string
    } else {
      return false
    }
  }

  get context(): PayButtonContext {
    return this.selectedPaymentType != null ? 'checkout' : 'default'
  }

  get showDonationForm(): boolean {
    return this.isModifiable && !this.giftee
  }

  get isPaymentLink() {
    return !this.isModifiable
  }

  @Watch('paymentDueByCreditCard', { immediate: true })
  updateCartWasEverFree() {
    // Compare to zero to allow credit from overpaid carts to be spent on donations.
    this.cartWasEverFree = this.cartWasEverFree || this.paymentDueByCreditCard === 0
  }

  onSubmit() {
    this.processing = true
    this.error = null
    this.paymentError = null
    this.lastInteractionWasCaptcha = false

    this.validate()
      .then(this.processCheckoutOnApi)
      .then(this.onSuccess)
      .catch(this.onError)
      .catch((error) => {
        // Any other error.
        this.error = 'Sorry, something unexpected happened. Please try again later.'

        // Rethrow it so TrackJS logs it.
        throw error
      })
      .finally(() => {
        this.submitting = false
        this.processing = false
        this.showMobileCart = false
      })
  }

  private async validate(): Promise<PaymentTokens> {
    let captchaToken = await this.validateCaptcha()
    /* eslint-disable no-console */
    console.log('captcha token', captchaToken?.substr(0, 5) + '...')
    await this.validateIdentityAndTerms()
    console.log('validated identity & terms')
    await this.validateGiftee()
    console.log('validated giftee')

    // Captcha can expire while waiting for the user to accept terms and conditions dialog.
    if (this.isCaptchaMissing()) {
      captchaToken = await this.validateCaptcha()
      /* eslint-disable no-console */
      console.log('revalidate captcha token', captchaToken?.substr(0, 5) + '...')
    }

    const paymentMethodID = await this.confirmPaymentMethod()
    console.log('got payment method', paymentMethodID?.substr(0, 5) + '...')

    // Throw error if captcha expires while confirming payment method to prevent Apple Pay and Google Pay
    // trying to checkout with expired captcha.
    // Can not validateCaptcha here as the Apple Pay or Google Pay dialog is still showing and blocks the
    // captcha challenge dialog.
    if (this.isCaptchaMissing()) {
      throw new CaptchaError('Captcha expired during checkout')
    }
    return { stripe: paymentMethodID, captcha: captchaToken }
  }

  isCaptchaMissing() {
    if (!captchaProvider || store.getters['Member/user']) {
      return false
    } else if (captchaProvider == 'hcaptcha') {
      return this.hcaptchaToken == null
    } else {
      return window.tixRecaptchaToken == null
    }
  }

  handleInputFocus() {
    if (!this.hasSentBeginCheckoutEvent) {
      this.hasSentBeginCheckoutEvent = true

      const tickets = store.getters['Cart/tickets'] as Ticket[]
      const templates = store.getters['Cart/eventTemplates'] as EventTemplate[]
      const types = store.getters['Cart/ticketTypes'] as TicketType[]
      const groups = store.getters['Cart/ticketGroups'] as TicketGroup[]
      const mods = store.getters['Cart/cartMods'] as CartMod[]

      const payload = getCartPayload(
        portal.default_currency_code,
        tickets.map((i) => i.id),
        tickets,
        templates,
        types,
        groups,
        mods,
      )
      triggerBeginCheckoutEvent(payload)
    }
  }

  private validateCaptcha(): Promise<string | undefined> {
    if (!captchaProvider || store.getters['Member/user']) {
      return Promise.resolve(undefined)
    } else if (this.hcaptchaToken) {
      return Promise.resolve(this.hcaptchaToken)
    } else if (window.tixRecaptchaToken) {
      return Promise.resolve(window.tixRecaptchaToken)
    } else {
      this.lastInteractionWasCaptcha = true
      if (captchaProvider == 'hcaptcha') {
        return this.getHcaptchaToken()
      } else {
        return this.getRecaptchaToken()
      }
    }
  }

  private getRecaptchaToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      window.tixRecaptchaHandleToken = (value) => {
        window.tixRecaptchaToken = value
        resolve(value)
      }
      window.tixRecaptchaHandleExpiry = () => {
        /* eslint-disable no-console */
        console.log('captcha token expired')
        window.tixRecaptchaToken = undefined
      }
      window.tixRecaptchaHandleError = () => {
        /* eslint-disable no-console */
        console.log('window.grecaptcha.getResponse()', window.grecaptcha.getResponse())
        reject(new CaptchaError())
      }

      this.captchaDismissObserver(reject)

      try {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute().then(
            () => {},
            (e) => {
              /* eslint-disable no-console */
              console.log('window.grecaptcha.execute().then()', e)
              throw e
            },
          )
        })
      } catch (e) {
        // @see https://blog.sentry.io/2016/05/17/what-is-script-error/#the-cause-cross-origin-scripts
        console.log('window.grecaptcha.ready try-catch', e)
        throw e
      }
    })
  }

  private getHcaptchaToken(): Promise<string> {
    const el = this.$refs.hcaptcha as VueHcaptcha
    return el
      .executeAsync()
      .then(({ response: token }) => {
        this.hcaptchaToken = token
        return this.hcaptchaToken
      })
      .catch((error) => {
        // challenge-closed; User closed the challenge.
        // challenge-expired; User took too long to solve the challenge.
        if (error == 'challenge-closed' || error == 'challenge-expired') {
          throw new ValidationError()
        } else {
          throw error
        }
      })
  }

  // Recaptcha doesn't have an on close handler so this is needed to detect clicks outside the captcha challenge that dismiss it.
  // Modified code from https://stackoverflow.com/a/72387948
  private captchaDismissObserver(reject) {
    const observer = new MutationObserver(() => {
      const iframe = document.querySelector('iframe[src^="https://www.google.com/recaptcha"][src*="bframe"]')

      if (iframe) {
        observer.disconnect()
        const captchaBackground = iframe.parentNode?.parentNode?.firstChild
        captchaBackground?.addEventListener('click', () => {
          reject(new CaptchaError('dismissed'))
        })
      }
    })

    observer.observe(document.documentElement || document.body, { childList: true, subtree: true })
  }

  // TODO Instead of using refs to validate child components, use form="" attribute
  // on form inputs to validate all form elements on native form-submit event.
  private validateIdentityAndTerms(): Promise<void> {
    const identityForm = this.$refs.identityForm as HTMLFormElement

    // Validate the identity form.
    return reportFormValidity(identityForm).then(() => {
      // Validate the terms and conditions.
      const termsCheckbox = this.$refs.terms as TermsCheckbox
      if (!termsCheckbox.accepted) {
        this.lastInteractionWasCaptcha = false
      }
      return termsCheckbox.validate()
    })
  }

  private validateGiftee(): Promise<void> {
    if (this.giftee && this.giftee.email === this.identityFormEmailAddress) {
      this.error =
        'You cannot gift a membership to yourself. Do you want to <membership-link>buy or renew a membership</membership-link>?'
      return Promise.reject(new ValidationError())
    } else {
      return Promise.resolve()
    }
  }

  private confirmPaymentMethod(): Promise<string | undefined> {
    this.submitting = true
    if (this.usesAfterpay) {
      // 'new_intent' will make the API generate a Stripe PaymentIntent which is used in the afterpay payment-workflow.
      return Promise.resolve('new_intent')
    } else if (this.stripePaymentRequired) {
      /* eslint-disable no-console */
      console.log('getting stripe payment method')
      return this.getStripePaymentMethod().then((paymentMethod) => {
        if (!paymentMethod.card) {
          /* eslint-disable no-console */
          console.log(paymentMethod, this.selectedPaymentType, this.supportedWallet, this.wallet)
          throw new Error('Non-card Stripe payment method')
          // TODO Remove the above data-gathering code and allow non-card payments?
          // return paymentMethod
        } else if (cardIsAllowed(paymentMethod.card)) {
          return paymentMethod.id
        } else {
          this.paymentError = this.t.cardBrandError
          throw new ValidationError()
        }
      })
    } else {
      // No payment required. E.g. Free carts
      return Promise.resolve(undefined)
    }
  }

  private getStripePaymentMethod(): Promise<PaymentMethod> {
    /* eslint-disable no-console */
    console.log('selected payment type', this.selectedPaymentType)
    if (this.selectedPaymentType === 'stripe_wallet') {
      if (this.lastInteractionWasCaptcha) {
        throw new ValidationError()
      }
      return this.wallet!.openPaymentDialog(this.paymentDueByCreditCard)
    } else {
      return authorizeCreditCard(this.stripe!, this.stripeCardElement!, identityDataForStripe(this.identityFormData))
    }
  }

  private processCheckoutOnApi(tokens: PaymentTokens): Promise<CompletedOrdersResponse> {
    // Captcha Token is only valid for 1 use so have to reset captcha after the API consumes the token.
    // Call resetCaptcha() here rather than in finally() as the token should not be reset if validation fails.
    resetReCaptcha()
    this.hcaptchaToken = null

    // Store the purchaser's email address on the component instance now to be passed on to complete page later.
    // @see getCheckoutCompleteLocation()
    this.purchaserEmailAddress = this.identityFormEmailAddress

    return this.storePurchaser()
      .then((purchaserId) => this.processPayments(tokens, purchaserId))
      .then((response) => {
        if ('ticket_order' in response) {
          return response
        } else {
          if (response.error?.payment_intent?.last_payment_error?.message) {
            this.paymentError = response.error.payment_intent.last_payment_error.message
          }
          throw new TixStripeError(response.error)
        }
      })
  }

  private storePurchaser(): Promise<string | undefined> {
    if (!this.showIdentityForm) {
      return Promise.resolve(undefined)
    } else {
      // Wallet identity values take precedence over form identity values.
      return upsertGuestAndGetId({
        ...this.identityFormData,
        ...this.wallet?.identityFormData,
      })
    }
  }

  private processPayments(
    tokens: PaymentTokens,
    purchaserId?: string,
  ): Promise<CompletedOrdersResponse | { paymentIntent?: PaymentIntent; error?: StripeError }> {
    const giftCards = store.getters['Cart/pendingGiftCardPayments']
    const creditCard = store.getters['Cart/paymentDueByCreditCard']
    const saveAndRenew = store.getters['Cart/autoRenewMembership']

    const payments = getPaymentPayloads(giftCards, creditCard, saveAndRenew, tokens)

    const options = this.checkoutAPIOptions(tokens, purchaserId)
    const afterpay = afterpayData(this.cart.id, this.identityFormData, purchaserId)
    return processPaymentPayloads(this.cart, this.stripe!, payments, options, afterpay)
  }

  checkoutAPIOptions(tokens: PaymentTokens, purchaserId?: string): CheckoutOptions {
    return {
      tokens,
      // TODO Better name for "injected" carts?
      injected: !this.isModifiable,
      suppressEmailNotification: this.orderId != null,
      purchaser: purchaserId,
      giftee: store.getters['Cart/giftee']?.id,
    }
  }

  private onSuccess(response: CompletedOrdersResponse) {
    this.wallet?.paymentSucceeded()

    // Push to GTM as soon as we have the order data.
    if (isGA4()) {
      const purchasePayloads = getPurchasePayloads(
        portal.default_currency_code,
        response,
        store.getters['Cart/eventTemplates'] as EventTemplate[],
        store.getters['Cart/ticketTypes'] as TicketType[],
        store.getters['Cart/cartMods'] as CartMod[],
      )
      triggerPurchaseEvents(purchasePayloads)
    } else {
      pushPurchaseEventToGoogleTagManager(
        response,
        store.getters['Cart/eventTemplates'] as EventTemplate[],
        store.getters['Cart/ticketTypes'] as TicketType[],
        store.getters['Cart/cartMods'] as CartMod[],
      )
    }

    // The cart and other Vue X stores are cleared in a global afterEach() handler.
    // @see afterCheckoutRoute()

    clearTimer()

    this.$router.push(this.getNextRoute(response))

    if (this.orderId) {
      API.post(`ticket_order/${this.orderId}/email`, {
        body: { log_context: 'order_update' },
      })
    }
  }

  onError(error) {
    this.wallet?.paymentFailed()
    if (error.captchaError) {
      if (error.message === 'dismissed') {
        // User clicked outside the captcha challenge.
        logInTrackJS('Captcha Challenge Dismissed', error)
        return Promise.resolve()
      } else {
        throw error
      }
    } else if (error.validationError) {
      // Only shown to screen-readers, inform users there were validation errors
      // Clear existing validation errors first, aria-live only reads errors when they are added to the page
      this.validationError = null
      this.validationError =
        'There were errors found in the information you submitted. Please address the errors and re-submit the form.'
      return Promise.resolve()
    } else if (error.stripeError) {
      // Maybe the customer cancelled Stripe's 3DS/2FA confirmation dialog or failed to confirm it.
      // A Stripe test card (4242 4242 4242 4242) in Stripe production surfaces a card_declined error here.
      // Regular card_declined errors are handled as tix-api errors.
      // TODO Reload/refresh the cart in this scenario?
      this.paymentError = error.originalStripeError.message
    } else if (error.response?.status === 402) {
      // This is necessary if some payments succeeded, and others failed. E.g. gift cards succeeded but credit
      // card failed.
      refreshStoredCart()

      // Handles payment processing errors from tix-api.
      // TODO Further filter these by credit-card payment, as distinct from gift card payment?
      this.paymentError = apiErrorMessageOrRethrow(error, 'payment_failed')
    } else {
      // Handle non-payment errors from tix-api.
      const apiError = getApiErrorEntity(error)
      if (apiError?._code === 'guest_account') {
        API.post('password_reset/request', { body: { email: this.identityFormEmailAddress } }).then(() => {
          this.error =
            'Oops! You have not set a password yet. Please check your email inbox for instructions to set a password, then try again.'
        })
      } else if (apiError?._code === 'incorrect_password') {
        this.error =
          'That is not the correct password. Please try again. <router-link to="/user/forgot-password">Forgot your password?</router-link>'
      } else {
        if (this.selectedPaymentType === 'stripe_wallet') {
          // Cart can expire while user is completing wallet payment.
          this.error = apiErrorMessageOrRethrow(error, 'validate_not_email,cart_expired')
        } else {
          this.error = apiErrorMessageOrRethrow(error, 'validate_not_email')
        }
      }
    }
  }

  get identityFormEmailAddress(): string {
    return this.identityFormData.email as string
  }

  getNextRoute(response: CompletedOrdersResponse) {
    if (this.orderId) {
      return `/manage/${this.orderId}`
    } else {
      return {
        name: 'checkout/complete',
        query: {
          orderIds: response.ticket_order._data.map((order) => order.id) as any,
        },
        // Vue Router's types only document string values for entries in params and query.
        // It supports complex data types nevertheless.
        // TODO Find a better way to pass this data along or provide better type-safety.
        // @see https://router.vuejs.org/guide/essentials/navigation.html
        params: {
          checkoutResponse: response as any,
          // TODO Remove this route parameter.
          // @see https://tixtrackteam.slack.com/archives/CP9C2ET5E/p1629076975036500
          purchaserEmailAddress: this.purchaserEmailAddress ?? (this.memberIdentity?.email as any),
        },
      }
    }
  }

  // Redirect to the homepage if the cart becomes empty. This occurs when items are removed or the cart expires.
  // TODO Tell the customer why they were redirected to the homepage?
  @Watch('cartHasItems')
  redirectHomeOnEmptyCart(hasItems) {
    if (!hasItems) {
      this.$router.push({ path: '/' })
    }
  }

  // Apply member's post code as discount code code either on load, or on login.
  @Watch('memberIdentity', { immediate: true })
  userChanged() {
    if (this.memberIdentity) {
      const fields = this.memberIdentity.meta
      const zipCode = fields.zip_code
      if (zipCode) {
        this.applyDiscountCodeFromZipCode(zipCode)
      }
    }
  }

  get validAfterpayCart() {
    return store.getters['Cart/ticketGroups'].some((tg) => tg.handler != 'donation' && tg.handler != 'ledger')
  }

  private applyDiscountCodeFromZipCode(zipCode) {
    if (this.zipCodeDiscountRegExp) {
      // Get the part of the post code that should be used as a discount code.
      // Customers may enter a full zip code like 60607-1234.
      const matches = zipCode.match(this.zipCodeDiscountRegExp)

      if (matches?.length) {
        // Require benefit so that any existing exclusive promo codes will persist if this one does not yield a benefit.
        applyPromoCode(matches[0])
          // Ignore errors.
          .catch(() => null)
      }
    }
  }

  private get emailToMessage(): string {
    return this.$t('checkoutRoute.ticketsEmailToMessage', {
      email: escapeHTML(this.injectedCartOwner.email),
    }) as string
  }

  get showApplyPromoCodes() {
    return environment.web.show_promo_codes_on_checkout && store.getters['Cart/paymentDueByCreditCard'] > 0
  }

  get captchaProvider() {
    // Captcha isn't required for logged in members.
    // Still show message to make it harder for bad actors to realise that logging in bypasses captcha.
    return captchaProvider
  }

  get hcaptchaPublicKey(): string {
    return environment.captcha.hcaptcha_public
  }
}
